import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiChevronLeft, HiChevronRight, HiOutlineCheckCircle, HiOutlineClock,
  HiOutlineExclamationTriangle, HiOutlineInformationCircle, HiOutlineLockClosed,
  HiOutlinePaperAirplane, HiOutlinePencilSquare,
} from 'react-icons/hi2';

import { apiService } from '../../components/Config/api.service';
import {
  dateLisible, decalerJours, formaterDuree, formaterEcart, jourLisible, moisLisible,
} from './formatFeuilleTemps';
import { dureeEnSaisie, lireDuree } from './saisieDuree';
import './MaFeuilleDeTemps.css';

/**
 * Ma feuille de temps (contrat C22, Yros/Kavero#207).
 *
 * La page Pointage ne garde que l'acte de pointer ; la semaine qu'on revoit
 * vit ici. Elle répond à trois questions, dans cet ordre : où en suis-je (le
 * résumé, qui porte l'unique action principale), qu'est-ce qui cloche (les
 * jours, chacun avec son état en toutes lettres) et comment le corriger (la
 * ligne elle-même, qui s'ouvre sur un éditeur).
 *
 * Odoo décide de tout ce qui se décide : le verrou d'une semaine soumise, les
 * jours à venir, les bornes des heures. La page masque ce qu'il interdit pour
 * ne pas tenter un refus certain, et affiche son message mot pour mot quand il
 * refuse quand même.
 *
 * Le premier chargement ne demande aucune semaine : c'est Odoo qui dit
 * laquelle est la courante, dans le fuseau du salarié. La navigation part de
 * cette semaine-là et ne va jamais au-delà.
 *
 * Mise en page : aucune largeur de fenêtre n'y décide d'une colonne. L'espace
 * collaborateur laisse environ 780 px au contenu, à côté de sa barre latérale,
 * quelle que soit la fenêtre : les lignes se replient d'elles-mêmes selon la
 * place qu'elles ont. Aucun texte sous 12 px, aucun gris sous 4,5:1.
 */

const BOUTON_BASE = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-base font-semibold transition-colors duration-150 motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-primary)]';
const BOUTON_PRINCIPAL = `${BOUTON_BASE} bg-[--color-primary] text-white shadow-sm hover:bg-[--bg-hover]`;
const BOUTON_SECONDAIRE = `${BOUTON_BASE} border border-slate-300 bg-white text-slate-800 hover:bg-slate-50`;
const LIEN = 'inline-flex min-h-[44px] items-center text-sm font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-primary)]';
const CHAMP = 'mt-1 block min-h-[44px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[16px] text-slate-900 placeholder:text-slate-500 focus:border-[color:var(--color-primary)] focus:outline focus:outline-2 focus:outline-[color:var(--color-primary)]';
const CARTE = 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6';
const ERREUR = 'mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800';

// Chaque ton est une paire fond clair / texte foncé à plus de 6:1, et chaque
// pastille porte un texte : la couleur n'est jamais seule à dire l'état.
const TONS = {
  vert: 'bg-emerald-50 text-emerald-800',
  ambre: 'bg-amber-50 text-amber-900',
  bleu: 'bg-blue-50 text-blue-800',
  gris: 'bg-slate-100 text-slate-700',
  primaire: 'bg-[--color-primary] text-white',
};

const ETAT_CLASSE = {
  en_attente: 'bg-amber-50 text-amber-900',
  validee: 'bg-emerald-50 text-emerald-800',
  refusee: 'bg-red-50 text-red-800',
};

const STATUT = {
  brouillon: TONS.gris,
  soumise: TONS.vert,
  renvoyee: TONS.ambre,
};

const Pastille = ({ ton, Icone, testid, children }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium ${TONS[ton]}`}
  >
    {Icone && <Icone className="h-4 w-4 shrink-0" aria-hidden="true" />}
    {children}
  </span>
);

// Zéro se lit « 0 h » : « 0 min » ferait croire à une mesure, un tiret à une
// donnée manquante.
const heures0 = (valeur, t) => (valeur ? formaterDuree(valeur, t) : t('feuilleTemps.heuresPleines', { heures: 0 }));

const AUJOURDHUI = () => new Date().toISOString().slice(0, 10);

const enMinutes = (heure) => {
  const parties = /^(\d{2}):(\d{2})$/.exec(String(heure ?? ''));
  return parties ? Number(parties[1]) * 60 + Number(parties[2]) : NaN;
};

// Le formulaire refuse ce que le salarié voit lui-même : une plage qui ne
// tient pas, un motif vide, un jour à venir. Le chevauchement, lui, n'est
// connu que d'Odoo.
const premierRefusDeclaration = (saisie) => {
  const debut = enMinutes(saisie.debut);
  const fin = enMinutes(saisie.fin);
  if (!Number.isFinite(debut) || !Number.isFinite(fin) || debut >= fin) return 'plageInvalide';
  if (!saisie.motif.trim()) return 'motifRequis';
  if (!saisie.date || saisie.date > AUJOURDHUI()) return 'jourAVenir';
  return null;
};

const SAISIE_VIDE = { date: '', debut: '', fin: '', motif: '' };

const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  return enveloppe?.message || t('feuilleTemps.erreurTechnique');
};

/**
 * L'état d'un jour, en un mot : c'est lui qui décide de ce que la ligne dit et
 * de la couleur qu'elle prend.
 * - `a-venir` : rien à dire, rien à corriger ;
 * - `corrige` : le salarié a posé sa propre durée ;
 * - `non-travaille` : ni horaire attendu ni heures ;
 * - `en-cours` : aujourd'hui — un pointage manquant n'y est pas encore une anomalie ;
 * - `sans-pointage` : un jour passé, attendu, où rien n'est compté ;
 * - `ok` : le reste.
 */
const etatDuJour = (jour, aujourdhui) => {
  if (jour.a_venir) return 'a-venir';
  if (jour.corrige) return 'corrige';
  if (!jour.attendu && !jour.retenu && !jour.pointe) return 'non-travaille';
  if (aujourdhui) return 'en-cours';
  if (!jour.retenu) return 'sans-pointage';
  return 'ok';
};

const FOND_JOUR = {
  ok: 'border-slate-200 bg-white',
  'en-cours': 'border-slate-300 bg-white',
  corrige: 'border-blue-200 bg-blue-50/40',
  'sans-pointage': 'border-amber-300 bg-amber-50/60',
  'non-travaille': 'border-slate-200 bg-slate-50',
  'a-venir': 'border-transparent bg-slate-50',
};

function MaFeuilleDeTemps() {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;

  const [semaine, setSemaine] = useState(null);
  const [base, setBase] = useState(null);
  const [probleme, setProbleme] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [correction, setCorrection] = useState(null);
  const [erreurCorrection, setErreurCorrection] = useState(null);
  const [erreurAction, setErreurAction] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [confirmationEnvoi, setConfirmationEnvoi] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [correctionEnCours, setCorrectionEnCours] = useState(false);
  const [declarationEnCours, setDeclarationEnCours] = useState(false);
  const [saisie, setSaisie] = useState(null);
  const [erreurSaisie, setErreurSaisie] = useState(null);
  const [succes, setSucces] = useState(false);
  const [focusApres, setFocusApres] = useState(null);
  const [focusEnvoi, setFocusEnvoi] = useState(null);

  const boutonEnvoyer = useRef(null);
  const boutonConfirmer = useRef(null);
  const champHeures = useRef(null);
  const lignesJour = useRef({});
  // La semaine que la dernière lecture demandait : « Réessayer » refait celle-là,
  // pas celle qui reste affichée.
  const derniereDemande = useRef(undefined);

  const charger = useCallback(async (lundi) => {
    derniereDemande.current = lundi;
    setEnCours(true);
    try {
      const reponse = await apiService.lireFeuilleTemps(lundi);
      setSemaine(reponse);
      setProbleme(null);
      setBase((precedent) => precedent || reponse?.lundi || null);
    } catch (erreur) {
      setProbleme(interpreterProbleme(erreur, t));
    } finally {
      setEnCours(false);
    }
  }, [t]);

  useEffect(() => { charger(undefined); }, [charger]);

  // Le curseur va sur les heures dès qu'un éditeur s'ouvre ; il revient sur la
  // ligne quand il se ferme, pour que le clavier ne reparte pas du haut de la page.
  useEffect(() => {
    if (correction?.date) champHeures.current?.focus();
  }, [correction?.date]);

  useEffect(() => {
    if (!focusApres) return;
    lignesJour.current[focusApres]?.focus();
    setFocusApres(null);
  }, [focusApres, semaine]);

  // Le bouton d'envoi cède sa place à la confirmation : sans cela, le focus
  // tombe sur la page et le clavier repart du haut.
  useEffect(() => {
    if (!focusEnvoi) return;
    (focusEnvoi === 'confirmer' ? boutonConfirmer : boutonEnvoyer).current?.focus();
    setFocusEnvoi(null);
  }, [focusEnvoi, confirmationEnvoi]);

  const reinitialiserSaisies = () => {
    // Une saisie ouverte parle d'une autre semaine : elle ne la suit pas.
    setCorrection(null);
    setErreurCorrection(null);
    setErreurAction(null);
    setConfirmation(null);
    setConfirmationEnvoi(false);
    setSaisie(null);
    setErreurSaisie(null);
    setSucces(false);
  };

  const naviguer = (pas) => {
    if (!semaine || !base) return;
    const lundi = decalerJours(semaine.lundi, 7 * pas);
    if (lundi > base) return;
    reinitialiserSaisies();
    charger(lundi);
  };

  const revenirACetteSemaine = () => {
    if (!base) return;
    reinitialiserSaisies();
    charger(base);
  };

  const modifiable = semaine?.etat !== 'soumise';

  const ouvrirCorrection = (jour) => {
    if (correction?.date === jour.date) {
      setCorrection(null);
      setErreurCorrection(null);
      return;
    }
    setConfirmation(null);
    setErreurCorrection(null);
    // Un jour où rien n'est compté part vide : proposer « 0 h » inviterait à
    // l'accepter. L'horaire prévu s'offre en un clic, à côté.
    setCorrection({
      date: jour.date,
      heures: jour.retenu ? dureeEnSaisie(jour.retenu) : '',
      motif: '',
    });
  };

  const fermerCorrection = (date) => {
    setCorrection(null);
    setErreurCorrection(null);
    setFocusApres(date);
  };

  // `dansFormulaire` dit où le refus se lit : sous le formulaire qui vient de
  // partir, ou au niveau de la semaine pour une annulation — un autre jour peut
  // avoir son formulaire ouvert, le refus ne le concerne pas.
  const envoyerCorrection = async (params, dansFormulaire) => {
    if (correctionEnCours) return;
    setCorrectionEnCours(true);
    setErreurAction(null);
    try {
      setSemaine(await apiService.corrigerJourFeuilleTemps({ lundi: semaine.lundi, ...params }));
      setCorrection(null);
      setErreurCorrection(null);
      setConfirmation(t(params.heures === null
        ? 'feuilleTemps.correctionAnnulee' : 'feuilleTemps.correctionEnregistree'));
      setFocusApres(params.date);
    } catch (erreur) {
      const message = interpreterProbleme(erreur, t);
      if (dansFormulaire) setErreurCorrection(message); else setErreurAction(message);
    } finally {
      setCorrectionEnCours(false);
    }
  };

  const corriger = async () => {
    const heures = lireDuree(correction.heures);
    if (heures === null) {
      setErreurCorrection(t('feuilleTemps.heuresInvalides'));
      return;
    }
    setErreurCorrection(null);
    await envoyerCorrection({ date: correction.date, heures, motif: correction.motif.trim() }, true);
  };

  // Le proxy exige les quatre clés du corps : l'annulation part sans motif.
  const annulerCorrection = (date) => envoyerCorrection({ date, heures: null, motif: '' }, false);

  const ouvrirConfirmationEnvoi = () => {
    setErreurAction(null);
    setConfirmationEnvoi(true);
    setFocusEnvoi('confirmer');
  };

  const fermerConfirmationEnvoi = () => {
    setConfirmationEnvoi(false);
    setFocusEnvoi('envoyer');
  };

  const envoyerSemaine = async () => {
    if (envoiEnCours) return;
    setEnvoiEnCours(true);
    setErreurAction(null);
    try {
      setSemaine(await apiService.soumettreFeuilleTemps(semaine.lundi));
      setCorrection(null);
      setSaisie(null);
    } catch (erreur) {
      setErreurAction(interpreterProbleme(erreur, t));
      setFocusEnvoi('envoyer');
    } finally {
      setEnvoiEnCours(false);
      setConfirmationEnvoi(false);
    }
  };

  const declarer = async () => {
    if (declarationEnCours) return;
    const refus = premierRefusDeclaration(saisie);
    if (refus) {
      setErreurSaisie(t(`feuilleTemps.${refus}`));
      return;
    }
    setErreurSaisie(null);
    setDeclarationEnCours(true);
    try {
      await apiService.creerDemandeRh({
        type: 'heures_sup',
        contenu: {
          date: saisie.date,
          heure_debut: saisie.debut,
          heure_fin: saisie.fin,
          motif: saisie.motif.trim(),
        },
      });
      setSaisie(null);
      setSucces(true);
      // Relire la semaine : la déclaration déposée doit apparaître dans la
      // liste sans rechargement de page.
      await charger(semaine.lundi);
    } catch (erreur) {
      setErreurSaisie(interpreterProbleme(erreur, t));
    } finally {
      setDeclarationEnCours(false);
    }
  };

  const modifierSaisie = (champ, valeur) => setSaisie((precedent) => ({ ...precedent, [champ]: valeur }));
  const modifierCorrection = (champ, valeur) => setCorrection((precedent) => ({ ...precedent, [champ]: valeur }));

  const blocProbleme = probleme && (
    <div
      data-testid="feuille-probleme"
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
    >
      <HiOutlineExclamationTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{probleme}</span>
      <button
        type="button"
        data-testid="feuille-reessayer"
        onClick={() => charger(derniereDemande.current)}
        className={BOUTON_SECONDAIRE}
      >
        {t('feuilleTemps.reessayer')}
      </button>
    </div>
  );

  // Sans semaine à montrer, l'erreur tient lieu de page ; avec une semaine,
  // elle s'affiche au-dessus d'elle pour que la navigation reste là.
  if (probleme && !semaine) {
    return (
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold text-slate-900">{t('feuilleTemps.titre')}</h2>
        {blocProbleme}
      </section>
    );
  }

  // Tant que la première réponse n'est pas là, une ébauche de la page : des
  // totaux à zéro se liraient comme une semaine sans heures, un texte nu comme
  // une page vide.
  if (!semaine) {
    return (
      <section data-testid="feuille-chargement" role="status" aria-busy="true" className="space-y-4">
        <span className="sr-only">{t('feuilleTemps.chargement')}</span>
        <div className="h-8 w-60 animate-pulse rounded-lg bg-slate-200 motion-reduce:animate-none" />
        <div className="h-44 animate-pulse rounded-2xl bg-slate-200 motion-reduce:animate-none" />
        <div className="h-80 animate-pulse rounded-2xl bg-slate-200 motion-reduce:animate-none" />
      </section>
    );
  }

  const jours = semaine.jours;
  const estSemaineCourante = semaine.lundi === base;
  // `a_venir` dit « après aujourd'hui » : aujourd'hui est donc le dernier jour
  // qui ne l'est pas, sur la semaine courante seulement.
  const joursEcoules = jours.filter((jour) => !jour.a_venir);
  const aujourdhui = estSemaineCourante && joursEcoules.length
    ? joursEcoules[joursEcoules.length - 1].date : null;
  const somme = (cle) => jours.reduce((total, jour) => total + (jour[cle] || 0), 0);
  const compte = somme('retenu');
  const prevu = somme('attendu');
  const progression = prevu > 0 ? Math.min(100, Math.round((compte / prevu) * 100)) : null;
  const aucuneHeure = semaine.source === 'horaire' ? 'aucuneHeure' : 'aucunPointage';

  const mois = semaine.mois || {};
  const resumeMois = [
    ['attendu', mois.attendu],
    ['fait', mois.fait],
    ['supplementaires', mois.supplementaires],
    ['manquantes', mois.manquantes],
  ];
  const heuresSup = semaine.heures_sup || [];

  // Ce que dit la ligne d'un jour, à droite de ses heures : l'état, jamais un tiret.
  const etiquetteJour = (jour, etat) => {
    // Un jour corrigé dit déjà « Corrigé », son pointé d'avant et son motif.
    if (etat === 'corrige') return null;
    if (etat === 'sans-pointage') {
      return <Pastille ton="ambre" Icone={HiOutlineExclamationTriangle}>{t(`feuilleTemps.${aucuneHeure}`)}</Pastille>;
    }
    if (etat === 'en-cours') {
      return (
        <Pastille ton="gris" Icone={HiOutlineClock}>
          {t(jour.retenu ? 'feuilleTemps.enCours' : 'feuilleTemps.pasEncorePointe')}
        </Pastille>
      );
    }
    if (etat === 'ok') {
      // L'écart vient d'Odoo, qui compare le compté au prévu avec les
      // tolérances de la société : le recalculer ici les ignorerait.
      if (jour.ecart) {
        const ecart = <Pastille ton={jour.ecart > 0 ? 'vert' : 'ambre'}>{formaterEcart(jour.ecart, t)}</Pastille>;
        // Yros/Kavero#491 : des heures en plus ne comptent qu'une fois validées.
        if (jour.validation !== 'a_valider' && jour.validation !== 'refusee') return ecart;
        return (
          <span className="inline-flex flex-wrap gap-1.5">
            {ecart}
            <Pastille ton={jour.validation === 'refusee' ? 'ambre' : 'gris'} testid={`feuille-validation-${jour.date}`}>
              {t(jour.validation === 'refusee' ? 'feuilleTemps.supRefusees' : 'feuilleTemps.supAValider')}
            </Pastille>
          </span>
        );
      }
      return <Pastille ton="vert" Icone={HiOutlineCheckCircle}>{t('feuilleTemps.conforme')}</Pastille>;
    }
    return null;
  };

  const rendreJour = (jour) => {
    const etat = etatDuJour(jour, jour.date === aujourdhui);
    const editable = modifiable && !jour.a_venir;
    const ouvert = correction?.date === jour.date;
    const estAujourdhui = jour.date === aujourdhui;
    const idEditeur = `feuille-editeur-${jour.date}`;

    // Un jour sans rien à dire tient sur une ligne.
    if (etat === 'a-venir' || (etat === 'non-travaille' && !editable)) {
      return (
        <li
          key={jour.date}
          data-testid={`feuille-jour-${jour.date}`}
          data-statut={jour.a_venir ? 'a-venir' : 'passe'}
          data-etat={etat}
          className={`overflow-hidden rounded-xl border ${FOND_JOUR[etat]}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3">
            <span className="text-base font-medium text-slate-700 first-letter:uppercase">
              {jourLisible(jour.date, langue)}
            </span>
            <span className="text-sm text-slate-600">
              {jour.attendu > 0
                ? t('feuilleTemps.aVenirPrevues', { heures: formaterDuree(jour.attendu, t) })
                : t('feuilleTemps.nonTravaille')}
            </span>
          </div>
        </li>
      );
    }

    // À gauche, le jour et son état dessous ; à droite, les heures, alignées sur
    // leurs chiffres ; au bout, l'icône qui dit que la ligne s'ouvre. Ce motif
    // tient à toutes les largeurs sans replier de colonne.
    const etiquette = etat === 'non-travaille'
      ? <span className="text-sm text-slate-600">{t('feuilleTemps.nonTravaille')}</span>
      : etiquetteJour(jour, etat);
    const corps = (
      <span className="flex w-full items-center gap-3">
        <span className="block min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-slate-900 first-letter:uppercase">
              {jourLisible(jour.date, langue)}
            </span>
            {estAujourdhui && (
              <Pastille ton="primaire" testid={`feuille-aujourdhui-${jour.date}`}>
                {t('feuilleTemps.aujourdhui')}
              </Pastille>
            )}
          </span>
          {(etiquette || (etat === 'sans-pointage' && editable)) && (
            <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              {etiquette}
              {etat === 'sans-pointage' && editable && (
                <span className="text-sm font-semibold text-[color:var(--color-primary)]">
                  {t('feuilleTemps.ajouterMesHeures')}
                </span>
              )}
            </span>
          )}
        </span>

        {etat !== 'non-travaille' && (
          <span className="block shrink-0 text-right tabular-nums">
            <span className="text-lg font-semibold text-slate-900">{heures0(jour.retenu, t)}</span>
            {jour.attendu > 0 && (
              <span className="ml-1.5 text-sm text-slate-600">/ {formaterDuree(jour.attendu, t)}</span>
            )}
          </span>
        )}

        {editable && <HiOutlinePencilSquare className="h-5 w-5 shrink-0 text-slate-600" aria-hidden="true" />}
        {editable && <span className="sr-only">{t('feuilleTemps.corrigerLeJour')}</span>}
      </span>
    );

    return (
      <li
        key={jour.date}
        data-testid={`feuille-jour-${jour.date}`}
        data-statut={jour.a_venir ? 'a-venir' : 'passe'}
        data-etat={etat}
        className={`overflow-hidden rounded-xl border ${FOND_JOUR[etat]}`}
      >
        {editable ? (
          <button
            type="button"
            ref={(element) => { lignesJour.current[jour.date] = element; }}
            data-testid={`feuille-corriger-${jour.date}`}
            aria-expanded={ouvert}
            aria-controls={ouvert ? idEditeur : undefined}
            onClick={() => ouvrirCorrection(jour)}
            className="block min-h-[44px] w-full px-4 py-3 text-left transition-colors duration-150 hover:bg-black/[0.03] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--color-primary)]"
          >
            {corps}
          </button>
        ) : (
          <div className="px-4 py-3">{corps}</div>
        )}

        {jour.corrige && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-3 text-sm text-slate-700">
            <Pastille ton="bleu" Icone={HiOutlinePencilSquare} testid={`feuille-corrige-${jour.date}`}>
              {t('feuilleTemps.corrige')}
            </Pastille>
            {jour.pointe > 0 && <span>{t('feuilleTemps.pointeAvant', { heures: formaterDuree(jour.pointe, t) })}</span>}
            {jour.motif_correction && <span>{jour.motif_correction}</span>}
            {modifiable && (
              <button
                type="button"
                data-testid={`feuille-annuler-correction-${jour.date}`}
                onClick={() => annulerCorrection(jour.date)}
                disabled={correctionEnCours}
                className={`${LIEN} ml-auto disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {t('feuilleTemps.annulerCorrection')}
              </button>
            )}
          </div>
        )}

        {editable && ouvert && (
          <div
            id={idEditeur}
            onKeyDown={(evenement) => { if (evenement.key === 'Escape') fermerCorrection(jour.date); }}
            className="feuille-deploiement border-t border-slate-200 bg-white px-4 py-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(11rem,14rem)_1fr]">
              <div>
                <label className="block text-sm font-medium text-slate-800">
                  {t('feuilleTemps.heures')}
                  <input
                    ref={champHeures}
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    data-testid="correction-heures"
                    value={correction.heures}
                    onChange={(evenement) => modifierCorrection('heures', evenement.target.value)}
                    aria-describedby={`${idEditeur}-apercu`}
                    className={CHAMP}
                  />
                </label>
                <p id={`${idEditeur}-apercu`} data-testid="correction-apercu" aria-live="polite" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
                  {(() => {
                    if (!correction.heures.trim()) return t('feuilleTemps.heuresExemple');
                    const lue = lireDuree(correction.heures);
                    return lue === null
                      ? t('feuilleTemps.apercuInvalide')
                      : t('feuilleTemps.apercu', { duree: heures0(lue, t) });
                  })()}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                  {jour.pointe > 0 && (
                    <span data-testid="correction-pointe">
                      {t('feuilleTemps.pointeAvant', { heures: formaterDuree(jour.pointe, t) })}
                    </span>
                  )}
                  {jour.attendu > 0 && (
                    <button
                      type="button"
                      data-testid="correction-prevu"
                      onClick={() => modifierCorrection('heures', dureeEnSaisie(jour.attendu))}
                      className={LIEN}
                    >
                      {t('feuilleTemps.utiliserHoraire', { heures: formaterDuree(jour.attendu, t) })}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800">
                  {t('feuilleTemps.motifCorrection')}
                  <textarea
                    rows={2}
                    maxLength={500}
                    data-testid="correction-motif"
                    value={correction.motif}
                    onChange={(evenement) => modifierCorrection('motif', evenement.target.value)}
                    aria-describedby={`${idEditeur}-motif-aide`}
                    className={CHAMP}
                  />
                </label>
                <p id={`${idEditeur}-motif-aide`} data-testid="correction-motif-aide" className="mt-1.5 text-sm text-slate-600">
                  {t(correction.motif.trim() ? 'feuilleTemps.motifAide' : 'feuilleTemps.motifRequisAide')}
                </p>
              </div>
            </div>

            {erreurCorrection && (
              <p data-testid="correction-erreur" role="alert" className={ERREUR}>{erreurCorrection}</p>
            )}

            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                data-testid="correction-annuler"
                onClick={() => fermerCorrection(jour.date)}
                className={BOUTON_SECONDAIRE}
              >
                {t('feuilleTemps.annuler')}
              </button>
              <button
                type="button"
                data-testid="correction-envoyer"
                onClick={corriger}
                disabled={!correction.motif.trim() || correctionEnCours}
                className={BOUTON_PRINCIPAL}
              >
                {correctionEnCours ? t('feuilleTemps.enregistrement') : t('feuilleTemps.enregistrer')}
              </button>
            </div>
          </div>
        )}
      </li>
    );
  };

  const messageStatut = () => {
    if (semaine.etat === 'soumise') {
      return semaine.soumise_le
        ? t('feuilleTemps.statutsMessage.soumise', { date: dateLisible(semaine.soumise_le.slice(0, 10), langue) })
        : t('feuilleTemps.statutsMessage.soumiseSansDate');
    }
    return t(`feuilleTemps.statutsMessage.${semaine.etat}`);
  };

  const IconeStatut = { brouillon: HiOutlineInformationCircle, soumise: HiOutlineLockClosed, renvoyee: HiOutlineExclamationTriangle }[semaine.etat] || HiOutlineInformationCircle;

  return (
    <section data-testid="feuille-temps" className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold text-slate-900">{t('feuilleTemps.titre')}</h2>
        <div className="flex flex-wrap items-center gap-3">
          {!estSemaineCourante && (
            <button
              type="button"
              data-testid="feuille-cette-semaine"
              onClick={revenirACetteSemaine}
              disabled={enCours}
              className={LIEN}
            >
              {t('feuilleTemps.cetteSemaine')}
            </button>
          )}
          <div className="flex items-center rounded-xl border border-slate-300 bg-white p-1">
            <button
              type="button"
              data-testid="feuille-precedent"
              onClick={() => naviguer(-1)}
              disabled={enCours}
              aria-label={t('feuilleTemps.semainePrecedente')}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-40 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--color-primary)]"
            >
              <HiChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <span data-testid="feuille-semaine" aria-live="polite" className="min-w-[12.5rem] px-2 text-center text-base font-medium text-slate-900">
              {t('feuilleTemps.semaine', {
                debut: dateLisible(semaine.lundi, langue),
                fin: dateLisible(decalerJours(semaine.lundi, 6), langue),
              })}
            </span>
            <button
              type="button"
              data-testid="feuille-suivant"
              onClick={() => naviguer(1)}
              disabled={enCours || semaine.lundi >= base}
              aria-label={t('feuilleTemps.semaineSuivante')}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-40 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--color-primary)]"
            >
              <HiChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {blocProbleme}

      <section data-testid="feuille-resume" aria-label={t('feuilleTemps.resumeTitre')} className={CARTE}>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm font-medium text-slate-600">{t('feuilleTemps.resumeTitre')}</p>
              <span
                data-testid="feuille-statut"
                className={`rounded-full px-2.5 py-1 text-sm font-medium ${STATUT[semaine.etat] || TONS.gris}`}
              >
                {t(`feuilleTemps.statuts.${semaine.etat}`)}
              </span>
            </div>
            <p className="mt-2 flex flex-wrap items-baseline gap-x-2 tabular-nums">
              <span data-testid="feuille-resume-compte" className="text-3xl font-semibold text-slate-900">
                {heures0(compte, t)}
              </span>
              <span data-testid="feuille-resume-prevu" className="text-base text-slate-600">
                {t('feuilleTemps.surPrevues', { heures: heures0(prevu, t) })}
              </span>
            </p>
          </div>

          {modifiable && (
            confirmationEnvoi ? (
              <div
                data-testid="feuille-soumettre-confirmation"
                role="group"
                aria-labelledby="feuille-confirmation-titre"
                onKeyDown={(evenement) => { if (evenement.key === 'Escape' && !envoiEnCours) fermerConfirmationEnvoi(); }}
                className="feuille-deploiement w-full rounded-xl border border-slate-300 bg-slate-50 p-4 sm:max-w-sm"
              >
                <p id="feuille-confirmation-titre" className="text-base font-semibold text-slate-900">
                  {t('feuilleTemps.confirmerEnvoiTitre')}
                </p>
                <p className="mt-1 text-sm text-slate-700">{t('feuilleTemps.confirmerEnvoiAide')}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    ref={boutonConfirmer}
                    data-testid="feuille-soumettre-confirmer"
                    onClick={envoyerSemaine}
                    disabled={envoiEnCours}
                    className={BOUTON_PRINCIPAL}
                  >
                    {envoiEnCours ? t('feuilleTemps.envoiEnCours') : t('feuilleTemps.confirmerEnvoi')}
                  </button>
                  <button
                    type="button"
                    data-testid="feuille-soumettre-annuler"
                    onClick={fermerConfirmationEnvoi}
                    disabled={envoiEnCours}
                    className={BOUTON_SECONDAIRE}
                  >
                    {t('feuilleTemps.annulerEnvoi')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                ref={boutonEnvoyer}
                data-testid="feuille-soumettre"
                onClick={ouvrirConfirmationEnvoi}
                className={BOUTON_PRINCIPAL}
              >
                <HiOutlinePaperAirplane className="h-5 w-5" aria-hidden="true" />
                {t(semaine.etat === 'renvoyee' ? 'feuilleTemps.resoumettre' : 'feuilleTemps.soumettre')}
              </button>
            )
          )}
        </div>

        {progression !== null && (
          <div
            role="progressbar"
            data-testid="feuille-progression"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progression}
            aria-label={t('feuilleTemps.progression', { compte: heures0(compte, t), prevu: heures0(prevu, t) })}
            className="mt-5 h-2.5 overflow-hidden rounded-full bg-slate-200"
          >
            <div
              className="h-full rounded-full bg-[--color-primary] transition-[width] duration-300 motion-reduce:transition-none"
              style={{ width: `${progression}%` }}
            />
          </div>
        )}

        <div
          data-testid="feuille-statut-message"
          role="status"
          className={`mt-5 flex items-start gap-3 rounded-xl px-4 py-3 text-sm ${STATUT[semaine.etat] || TONS.gris}`}
        >
          <IconeStatut className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p>{messageStatut()}</p>
            {semaine.etat === 'renvoyee' && semaine.motif_renvoi && (
              <p data-testid="feuille-motif-renvoi" className="mt-1 font-semibold">
                {t('feuilleTemps.motifRenvoi')} {semaine.motif_renvoi}
              </p>
            )}
            {semaine.source === 'horaire' && (
              <p data-testid="feuille-source-horaire" className="mt-1">{t('feuilleTemps.sourceHoraire')}</p>
            )}
          </div>
        </div>

        {erreurAction && (
          <p data-testid="feuille-soumettre-erreur" role="alert" className={ERREUR}>{erreurAction}</p>
        )}
      </section>

      <section className={CARTE}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          <h3 className="text-lg font-semibold text-slate-900">{t('feuilleTemps.joursTitre')}</h3>
          <p className="text-sm text-slate-600">{t('feuilleTemps.legendeHeures')}</p>
        </div>
        {modifiable && (
          <p data-testid="feuille-jours-aide" className="mt-1 text-sm text-slate-600">{t('feuilleTemps.joursAide')}</p>
        )}

        {confirmation && (
          <p
            data-testid="feuille-confirmation"
            role="status"
            className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800"
          >
            <HiOutlineCheckCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            {confirmation}
          </p>
        )}

        <ul data-testid="feuille-jours" className="mt-4 space-y-2">
          {jours.map(rendreJour)}
        </ul>
      </section>

      <section className={CARTE}>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">{t('feuilleTemps.declarations')}</h3>
            <p data-testid="feuille-declarations-aide" className="mt-1 text-sm text-slate-600">
              {t('feuilleTemps.declarationsAide')}
            </p>
          </div>
          {modifiable && !saisie && (
            <button
              type="button"
              data-testid="feuille-declarer"
              onClick={() => { setSaisie({ ...SAISIE_VIDE, date: AUJOURDHUI() }); setSucces(false); }}
              className={BOUTON_SECONDAIRE}
            >
              {t('feuilleTemps.declarer')}
            </button>
          )}
        </div>

        {modifiable && saisie && (
          <div className="feuille-deploiement mt-4 rounded-xl bg-slate-50 p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[['date', 'jour', 'date'], ['debut', 'de', 'time'], ['fin', 'a', 'time']].map(
                ([champ, cle, genre]) => (
                  <label key={champ} className="block text-sm font-medium text-slate-800">
                    {t(`feuilleTemps.${cle}`)}
                    <input
                      type={genre}
                      data-testid={`declaration-${champ}`}
                      value={saisie[champ]}
                      max={genre === 'date' ? AUJOURDHUI() : undefined}
                      onChange={(evenement) => modifierSaisie(champ, evenement.target.value)}
                      className={CHAMP}
                    />
                  </label>
                ))}
              <label className="col-span-2 block text-sm font-medium text-slate-800 sm:col-span-1">
                {t('feuilleTemps.motif')}
                <input
                  type="text"
                  data-testid="declaration-motif"
                  value={saisie.motif}
                  maxLength={500}
                  onChange={(evenement) => modifierSaisie('motif', evenement.target.value)}
                  className={CHAMP}
                />
              </label>
            </div>
            <p data-testid="declaration-duree" aria-live="polite" className="mt-2 min-h-[1.25rem] text-sm font-medium text-slate-700">
              {(() => {
                const debut = enMinutes(saisie.debut);
                const fin = enMinutes(saisie.fin);
                return Number.isFinite(debut) && Number.isFinite(fin) && fin > debut
                  ? t('feuilleTemps.dureeDeclaree', { duree: formaterDuree((fin - debut) / 60, t) }) : '';
              })()}
            </p>
            {erreurSaisie && (
              <p data-testid="declaration-erreur" role="alert" className={ERREUR}>{erreurSaisie}</p>
            )}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                data-testid="declaration-annuler"
                onClick={() => { setSaisie(null); setErreurSaisie(null); }}
                className={BOUTON_SECONDAIRE}
              >
                {t('feuilleTemps.annuler')}
              </button>
              <button
                type="button"
                data-testid="declaration-envoyer"
                onClick={declarer}
                disabled={declarationEnCours}
                className={BOUTON_PRINCIPAL}
              >
                {declarationEnCours ? t('feuilleTemps.envoiEnCours') : t('feuilleTemps.envoyer')}
              </button>
            </div>
          </div>
        )}

        {succes && (
          <p data-testid="declaration-succes" role="status" className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
            <HiOutlineCheckCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('feuilleTemps.succesDeclaration')}
          </p>
        )}

        {heuresSup.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">{t('feuilleTemps.aucuneDeclaration')}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {heuresSup.map((declaration) => (
              <li
                key={declaration.id}
                data-testid={`feuille-declaration-${declaration.id}`}
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-base text-slate-900">
                    <span className="inline-block font-semibold first-letter:uppercase">{jourLisible(declaration.date, langue)}</span>
                    {' · '}{declaration.heure_debut}–{declaration.heure_fin}
                    {' · '}{formaterDuree(declaration.heures, t)}
                  </span>
                  <span className={`self-start rounded-full px-2.5 py-1 text-sm font-medium ${ETAT_CLASSE[declaration.etat] || ''}`}>
                    {t(`feuilleTemps.etats.${declaration.etat}`)}
                  </span>
                </div>
                <p className="mt-1 text-slate-700">{declaration.motif}</p>
                {declaration.motif_refus && (
                  <p className="mt-1 font-medium text-red-800">{declaration.motif_refus}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={CARTE}>
        <h3 data-testid="feuille-mois-titre" className="text-lg font-semibold text-slate-900">
          {t('feuilleTemps.moisTitre')}
          {mois.mois && (
            <span className="ml-2 text-base font-normal text-slate-600 first-letter:uppercase">
              {moisLisible(mois.mois, langue)}
            </span>
          )}
        </h3>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {resumeMois.map(([cle, valeur]) => (
            <div key={cle} data-testid={`feuille-mois-${cle}`} className="rounded-xl bg-slate-50 px-4 py-3">
              <span className="block text-sm font-medium text-slate-600">{t(`feuilleTemps.${cle}`)}</span>
              <span className="mt-1 block text-2xl font-semibold tabular-nums text-slate-900">{formaterDuree(valeur ?? 0, t)}</span>
            </div>
          ))}
        </div>
        {mois.supplementaires_a_valider > 0 && (
          <p data-testid="feuille-mois-a-valider" className="mt-3 text-sm text-slate-600">
            {t('feuilleTemps.supEnAttente', {
              validees: formaterDuree(mois.supplementaires_validees ?? 0, t),
              aValider: formaterDuree(mois.supplementaires_a_valider, t),
            })}
          </p>
        )}
        {/* Le compteur d'Odoo n'existe que si la société compte ses heures sup. */}
        {mois.solde_cumule != null && (
          <p className="mt-4 text-sm text-slate-600">
            {t('feuilleTemps.solde')}{' '}
            <span data-testid="feuille-solde" className="font-semibold text-slate-900">
              {formaterDuree(mois.solde_cumule, t)}
            </span>
          </p>
        )}
      </section>
    </section>
  );
}

export default MaFeuilleDeTemps;
